import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Plus, ChefHat, Users, Trash2, Clock, Loader2, BookOpen, Edit3, X,
} from "lucide-react";
import { cookbookApi, contributorApi } from "../api/cookbook";

export default function CookbookStudio() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingPartId, setEditingPartId] = useState(null);
  const [showNewPart, setShowNewPart] = useState(false);
  const [showNewRecipe, setShowNewRecipe] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, rcs, mems] = await Promise.all([
        cookbookApi.get(projectId),
        cookbookApi.listRecipes(projectId, { limit: 200 }),
        contributorApi.list(projectId).catch(() => []),
      ]);
      setOverview(ov);
      setRecipes(rcs.items || []);
      setMembers(mems || []);
    } catch (e) {
      toast.error(`Failed to load cookbook: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const parts = overview?.structure?.parts || [];
  const myRole = useMemo(() => {
    const me = JSON.parse(localStorage.getItem("shweebum_user") || "{}");
    const row = members.find((m) => m.user_id === me.id && m.status === "active");
    return row?.role || null;
  }, [members]);

  const canEdit = myRole === "owner" || myRole === "editor";
  const canManage = myRole === "owner";

  const addPart = async (title) => {
    try {
      await cookbookApi.addPart(projectId, { title });
      toast.success(`Added part "${title}"`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const addSection = async (partId, title) => {
    try {
      await cookbookApi.addSection(projectId, partId, { title });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const removePart = async (partId) => {
    if (!window.confirm("Delete this part? Its recipes stay but become unbinned.")) return;
    try {
      await cookbookApi.removePart(projectId, partId);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const removeSection = async (partId, sectionId) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      await cookbookApi.removeSection(projectId, partId, sectionId);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const deleteRecipe = async (rid) => {
    if (!window.confirm("Delete this recipe?")) return;
    try {
      await cookbookApi.deleteRecipe(projectId, rid);
      toast.success("Recipe removed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center" data-testid="cookbook-loading">
        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-12 text-center" data-testid="cookbook-not-found">
        <h2 className="text-xl">Cookbook not found</h2>
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mt-4">Back</Button>
      </div>
    );
  }

  const project = overview.project;
  const structure = overview.structure || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="cookbook-studio-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-gradient-to-r from-amber-950/40 via-slate-900 to-slate-900 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="cookbook-back-btn" className="shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-amber-400 shrink-0" />
                <h1 className="text-base sm:text-xl font-bold tracking-tight truncate" data-testid="cookbook-title">{project.title}</h1>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {structure.style?.replace("_", " ") || "single author"}
                {structure.cuisine ? ` · ${structure.cuisine}` : ""}
                {` · ${overview.recipe_count} recipe${overview.recipe_count === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px] sm:text-xs" data-testid="cookbook-role-badge">
              {myRole || "viewer"}
            </Badge>
            {canManage && (
              <Dialog open={showInvite} onOpenChange={setShowInvite}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-amber-500/40 text-amber-300" data-testid="cookbook-invite-btn">
                    <Users className="w-4 h-4 mr-2" /> Members ({members.length})
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>Members &amp; invitations</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">
                      Invite editors, reviewers, and contributors to this cookbook.
                    </DialogDescription>
                  </DialogHeader>
                  <ContributorPanel
                    projectId={projectId}
                    members={members}
                    onChanged={refresh}
                    canManage={canManage}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Intro */}
        {structure.introduction && (
          <p className="text-slate-300 italic mb-6 leading-relaxed border-l-2 border-amber-500/40 pl-4">
            {structure.introduction}
          </p>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap gap-2 mb-6">
          {canEdit && (
            <Dialog open={showNewPart} onOpenChange={setShowNewPart}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-amber-500/40 text-amber-300" data-testid="cookbook-new-part-btn">
                  <Plus className="w-4 h-4 mr-2" /> New part
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                <DialogHeader>
                  <DialogTitle>Add a part</DialogTitle>
                  <DialogDescription className="text-slate-400 text-xs">
                    Top-level grouping for recipes (e.g. Breakfast, Holiday Recipes).
                  </DialogDescription>
                </DialogHeader>
                <SimpleAddForm
                  placeholder="Breakfast"
                  buttonText="Add part"
                  onSubmit={async (title) => {
                    await addPart(title);
                    setShowNewPart(false);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}

          {canEdit && parts.length > 0 && (
            <Dialog open={showNewRecipe} onOpenChange={setShowNewRecipe}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500" data-testid="cookbook-new-recipe-btn">
                  <ChefHat className="w-4 h-4 mr-2" /> New recipe
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add a recipe</DialogTitle>
                  <DialogDescription className="text-slate-400 text-xs">
                    Quick-add. You can edit ingredients and steps after creation.
                  </DialogDescription>
                </DialogHeader>
                <RecipeForm
                  parts={parts}
                  onSubmit={async (payload) => {
                    try {
                      await cookbookApi.createRecipe(projectId, payload);
                      toast.success("Recipe added");
                      setShowNewRecipe(false);
                      refresh();
                    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Empty state */}
        {parts.length === 0 && recipes.length === 0 && (
          <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="cookbook-empty-state">
            <ChefHat className="w-12 h-12 mx-auto text-amber-700 mb-4" />
            <h3 className="text-lg font-semibold text-slate-300">Your cookbook is empty</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Start by adding a Part — like &quot;Breakfast&quot; or &quot;Holiday Recipes&quot; — then add recipes inside it.
            </p>
          </div>
        )}

        {/* Parts → Sections → Recipes */}
        <div className="space-y-6">
          {parts.map((part) => {
            const partRecipes = recipes.filter((r) => r.part_id === part.id);
            return (
              <div key={part.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-5" data-testid={`cookbook-part-${part.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-amber-300">{part.title}</h2>
                    {part.description && <p className="text-xs text-slate-500">{part.description}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setEditingPartId(editingPartId === part.id ? null : part.id)}
                        data-testid={`cookbook-part-add-section-${part.id}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Section
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-400"
                        onClick={() => removePart(part.id)}
                        data-testid={`cookbook-part-delete-${part.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                {editingPartId === part.id && (
                  <SimpleAddForm
                    placeholder="Section title (e.g. Pancakes)"
                    buttonText="Add section"
                    inline
                    onSubmit={async (title) => {
                      await addSection(part.id, title);
                      setEditingPartId(null);
                    }}
                  />
                )}

                {/* Sections under this part */}
                {(part.sections || []).map((sec) => {
                  const secRecipes = partRecipes.filter((r) => r.section_id === sec.id);
                  return (
                    <div key={sec.id} className="mt-4 pl-4 border-l border-slate-700" data-testid={`cookbook-section-${sec.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wide">{sec.title}</h3>
                        {canEdit && (
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-600 hover:text-red-400"
                            onClick={() => removeSection(part.id, sec.id)}
                            data-testid={`cookbook-section-delete-${sec.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <RecipeGrid recipes={secRecipes} onEdit={setEditingRecipeId} onDelete={deleteRecipe} canEdit={canEdit} />
                    </div>
                  );
                })}

                {/* Recipes directly in part (no section) */}
                {(() => {
                  const directRecipes = partRecipes.filter((r) => !r.section_id);
                  return directRecipes.length > 0 ? (
                    <div className="mt-3">
                      <RecipeGrid recipes={directRecipes} onEdit={setEditingRecipeId} onDelete={deleteRecipe} canEdit={canEdit} />
                    </div>
                  ) : null;
                })()}
              </div>
            );
          })}

          {/* Unbinned recipes (no part assigned) */}
          {(() => {
            const unbinned = recipes.filter((r) => !r.part_id);
            return unbinned.length > 0 ? (
              <div className="bg-slate-900/30 border border-dashed border-slate-700 rounded-lg p-5" data-testid="cookbook-unbinned">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Unbinned recipes</h3>
                <RecipeGrid recipes={unbinned} onEdit={setEditingRecipeId} onDelete={deleteRecipe} canEdit={canEdit} />
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Recipe edit modal */}
      {editingRecipeId && (
        <Dialog open onOpenChange={() => setEditingRecipeId(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit recipe</DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                Adjust ingredients, instructions, and metadata.
              </DialogDescription>
            </DialogHeader>
            <RecipeEditor
              projectId={projectId}
              recipeId={editingRecipeId}
              parts={parts}
              onSaved={() => { setEditingRecipeId(null); refresh(); }}
              canEdit={canEdit}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function RecipeGrid({ recipes, onEdit, onDelete, canEdit }) {
  if (recipes.length === 0) {
    return <p className="text-xs text-slate-600 italic">No recipes here yet.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cookbook-recipe-grid">
      {recipes.map((r) => (
        <div
          key={r.id}
          className="group bg-slate-900 border border-slate-800 rounded-lg p-4 hover:border-amber-500/40 transition-colors cursor-pointer"
          onClick={() => onEdit(r.id)}
          data-testid={`recipe-card-${r.id}`}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="font-semibold text-slate-100 line-clamp-2">{r.title || "(untitled)"}</h4>
            {canEdit && (
              <Button
                size="icon" variant="ghost"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 flex-shrink-0"
                onClick={(e) => { e.stopPropagation(); onDelete(r.id); }}
                data-testid={`recipe-delete-${r.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {r.description && <p className="text-xs text-slate-500 line-clamp-2 mb-2">{r.description}</p>}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            {r.total_time_min && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.total_time_min}m</span>}
            {r.servings && <span>· {r.servings} serv</span>}
            {r.category && <Badge variant="outline" className="text-[10px] border-slate-700">{r.category}</Badge>}
            {r.source_vault_item_id && <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">from vault</Badge>}
            {r.status === "submitted" && <Badge variant="outline" className="text-[10px] border-amber-700 text-amber-400">submitted</Badge>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SimpleAddForm({ placeholder, buttonText, onSubmit, inline = false }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      await onSubmit(value.trim());
      setValue("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={inline ? "flex gap-2" : "space-y-3"}>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        data-testid="cookbook-simple-add-input"
      />
      <Button onClick={submit} disabled={busy || !value.trim()} className={inline ? "" : "w-full"} data-testid="cookbook-simple-add-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} {buttonText}
      </Button>
    </div>
  );
}

function RecipeForm({ parts, onSubmit, initial = {} }) {
  const [title, setTitle] = useState(initial.title || "");
  const [description, setDescription] = useState(initial.description || "");
  const [category, setCategory] = useState(initial.category || "");
  const [partId, setPartId] = useState(initial.part_id || (parts[0]?.id || ""));
  const [sectionId, setSectionId] = useState(initial.section_id || "");
  const [prepTime, setPrepTime] = useState(initial.prep_time_min || "");
  const [cookTime, setCookTime] = useState(initial.cook_time_min || "");
  const [servings, setServings] = useState(initial.servings || "");
  const [ingredients, setIngredients] = useState((initial.ingredients || []).map((i) => i.text || "").join("\n"));
  const [instructions, setInstructions] = useState((initial.instructions || []).map((s) => s.text || "").join("\n"));
  const [busy, setBusy] = useState(false);

  const sections = useMemo(() => {
    const p = parts.find((x) => x.id === partId);
    return p?.sections || [];
  }, [parts, partId]);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await onSubmit({
        title: title.trim(),
        description,
        category,
        part_id: partId || null,
        section_id: sectionId || null,
        prep_time_min: prepTime ? parseInt(prepTime, 10) : null,
        cook_time_min: cookTime ? parseInt(cookTime, 10) : null,
        total_time_min: (prepTime && cookTime) ? parseInt(prepTime, 10) + parseInt(cookTime, 10) : null,
        servings: servings ? parseInt(servings, 10) : null,
        ingredients: ingredients.split("\n").map((t) => t.trim()).filter(Boolean).map((text) => ({ text })),
        instructions: instructions.split("\n").map((t) => t.trim()).filter(Boolean).map((text, idx) => ({ step: idx + 1, text })),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Buttermilk Pancakes" data-testid="recipe-form-title" />
      </div>
      <div>
        <Label className="text-xs">Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="recipe-form-description" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Part</Label>
          <Select value={partId} onValueChange={(v) => { setPartId(v); setSectionId(""); }}>
            <SelectTrigger data-testid="recipe-form-part"><SelectValue placeholder="Pick a part" /></SelectTrigger>
            <SelectContent>
              {parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Section (optional)</Label>
          <Select value={sectionId || "_none"} onValueChange={(v) => setSectionId(v === "_none" ? "" : v)}>
            <SelectTrigger data-testid="recipe-form-section"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— none —</SelectItem>
              {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div>
          <Label className="text-xs">Prep (min)</Label>
          <Input type="number" value={prepTime} onChange={(e) => setPrepTime(e.target.value)} data-testid="recipe-form-prep" />
        </div>
        <div>
          <Label className="text-xs">Cook (min)</Label>
          <Input type="number" value={cookTime} onChange={(e) => setCookTime(e.target.value)} data-testid="recipe-form-cook" />
        </div>
        <div>
          <Label className="text-xs">Servings</Label>
          <Input type="number" value={servings} onChange={(e) => setServings(e.target.value)} data-testid="recipe-form-servings" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Category</Label>
        <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="breakfast / dessert / drink…" data-testid="recipe-form-category" />
      </div>
      <div>
        <Label className="text-xs">Ingredients (one per line)</Label>
        <Textarea value={ingredients} onChange={(e) => setIngredients(e.target.value)} rows={5} placeholder="2 cups flour&#10;1.5 cups buttermilk" data-testid="recipe-form-ingredients" />
      </div>
      <div>
        <Label className="text-xs">Instructions (one per line)</Label>
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={5} placeholder="Whisk dry ingredients&#10;Add wet, stir until just combined" data-testid="recipe-form-instructions" />
      </div>
      <Button onClick={submit} disabled={busy || !title.trim()} className="w-full" data-testid="recipe-form-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChefHat className="w-4 h-4 mr-2" />} Save recipe
      </Button>
    </div>
  );
}

function RecipeEditor({ projectId, recipeId, parts, onSaved, canEdit }) {
  const [recipe, setRecipe] = useState(null);
  useEffect(() => {
    cookbookApi.getRecipe(projectId, recipeId).then(setRecipe).catch(() => setRecipe(null));
  }, [projectId, recipeId]);
  if (!recipe) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8 text-slate-500" />;
  if (!canEdit) {
    return (
      <div className="space-y-2 text-sm">
        <h3 className="text-lg font-semibold">{recipe.title}</h3>
        {recipe.description && <p className="text-slate-400">{recipe.description}</p>}
        <h4 className="font-medium text-slate-300 mt-3">Ingredients</h4>
        <ul className="list-disc list-inside text-slate-400">
          {(recipe.ingredients || []).map((i, idx) => <li key={idx}>{i.text}</li>)}
        </ul>
        <h4 className="font-medium text-slate-300 mt-3">Instructions</h4>
        <ol className="list-decimal list-inside text-slate-400 space-y-1">
          {(recipe.instructions || []).map((s, idx) => <li key={idx}>{s.text}</li>)}
        </ol>
      </div>
    );
  }
  return (
    <RecipeForm
      parts={parts}
      initial={recipe}
      onSubmit={async (payload) => {
        await cookbookApi.updateRecipe(projectId, recipeId, payload);
        toast.success("Saved");
        onSaved();
      }}
    />
  );
}

function ContributorPanel({ projectId, members, onChanged, canManage }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("contributor");
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await contributorApi.invite(projectId, { email: email.trim(), role });
      toast.success("Invitation sent");
      setEmail("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (cid) => {
    if (!window.confirm("Revoke this member's access?")) return;
    try {
      await contributorApi.revoke(projectId, cid);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-3">
      {canManage && (
        <div className="space-y-2 border-b border-slate-800 pb-3">
          <Label className="text-xs">Invite by email</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com" type="email"
              data-testid="invite-email-input"
            />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="sm:w-36" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="contributor">Contributor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={invite} disabled={busy || !email.trim()} data-testid="invite-submit" className="shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invite"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {members.length === 0 && <p className="text-xs text-slate-500">No members yet.</p>}
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between bg-slate-800/50 rounded p-2" data-testid={`member-row-${m.id}`}>
            <div className="min-w-0">
              <p className="text-sm truncate">{m.invited_email || m.user_id}</p>
              <p className="text-[10px] text-slate-500">
                {m.role} · {m.status}
              </p>
            </div>
            {canManage && m.role !== "owner" && (
              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-400"
                onClick={() => revoke(m.id)} data-testid={`member-revoke-${m.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
